import { useQuery } from "@tanstack/react-query";
import { ATTRAPPE, hole } from "./klient";
import { heuteImBetrieb, tagPlus as tagPlusImBetrieb } from "./betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Nachschlagen — Formen, Weg und Haken für „ist das Gerät frei?"

   **Eine eigene Datei, wie bei der Einsatzzeit.** Der Schirm hat seinen
   eigenen Weg (`GET /nachschlagen`) und seine eigene Maske; was er
   braucht, steht beisammen, statt in `typen.ts`, `routen.ts` und
   `haken.ts` verteilt zu liegen. Angeglichen wird trotzdem hier und nur
   hier: In der Maske steht kein Feldname aus der Datenbank.

   **Sichtbarer Text kommt nicht aus der API.** Die Antwort trägt `lage`,
   `text_key` und Datumsangaben; die Sätze baut die Maske
   (docs/34-api.md).
   ══════════════════════════════════════════════════════════════════ */

type Roh = Record<string, unknown>;

const alsListe = (wert: unknown): Roh[] => (Array.isArray(wert) ? (wert as Roh[]) : []);
const alsObjekt = (wert: unknown): Roh =>
  (typeof wert === "object" && wert !== null && !Array.isArray(wert) ? (wert as Roh) : {});
const txt = (wert: unknown): string =>
  wert === null || wert === undefined ? "" : typeof wert === "string" ? wert : String(wert);
const zahl = (wert: unknown): number => {
  const n = typeof wert === "number" ? wert : Number(wert);
  return Number.isFinite(n) ? n : 0;
};
const tagOderNull = (wert: unknown): string | null => (typeof wert === "string" && wert ? wert : null);

/** Die drei Lagen aus dem Brett — nicht frei/belegt (packages/fach). */
export type Lage = "geht" | "teils" | "nein";

/**
 * Was an **einem Tag** los ist — die vier Wörter des Tagesstreifens.
 *
 * Dieselben wie in der Schnellerfassung (`stile/schnell.css`, `.se-tag`), und
 * dieselben, die `dienste/nachschlagen.ts` schickt: Der Streifen wird drüben
 * gerechnet, aus demselben `belegt`, aus dem auch „ab wann wieder frei"
 * entsteht. Eine zweite Zählung im Browser wäre die zweite Wahrheit.
 */
export type Tageslage = "frei" | "teils" | "weich" | "belegt";

const alsTageslage = (wert: unknown): Tageslage =>
  wert === "teils" || wert === "weich" || wert === "belegt" ? wert : "frei";

const alsStreifen = (wert: unknown): Tageslage[] =>
  Array.isArray(wert) ? (wert as unknown[]).map(alsTageslage) : [];

/** Ein einzelnes Stück in der aufgeklappten Zeile. */
export type Geraetelage = {
  id: string;
  kennung: string;
  lage: Lage;
  textKey: string;
  tageVergeben: number;
  /** In der Werkstatt: zählt zum Bestand, nicht zur Verfügbarkeit. */
  werkstatt: boolean;
  /** Ab wann es die gefragte Dauer am Stück frei ist. */
  freiAb: string | null;
  /** Wer es im gefragten Zeitraum hat. */
  kunde: string;
  /**
   * Ein Angebot hält es für seine Bindefrist (18.09.2026). Frei wäre es —
   * aber es zu nehmen ist eine Entscheidung, die jemand bestätigen muss.
   */
  reserviert: {
    vorgang: string; kunde: string; frist: string;
    /** Kein Angebot, sondern ein Auftrag im Abschluss (N7-05). */
    imAbschluss: boolean;
    /** … und ein Kollege schließt ihn gerade ab — nicht zu nehmen. */
    abschlussLaeuft: boolean;
  } | null;
  /** Je Tag des Fensters eine Lage — der Streifen an diesem Stück. */
  tage: Tageslage[];
};

/** Eine Zeile je Maschinentyp — die Frage lautet „habt ihr einen Radlader". */
export type Typenlage = {
  typId: string;
  typ: string;
  gruppe: string;
  gruppeId: string;
  uebergruppe: string;
  lage: Lage;
  textKey: string;
  bestand: number;
  frei: number;
  /** Wie viele frei wären, wenn nicht ein Angebot sie hielte (18.09.2026). */
  reserviert: number;
  /** Davon durch einen Auftrag im Abschluss vorgemerkt, nicht durch ein Angebot (N7-05). */
  imAbschluss: number;
  /** Davon gerade in einem laufenden Abschluss — kein „Trotzdem nehmen". */
  abschlussLaeuft: number;
  teils: number;
  werkstatt: number;
  freiAb: string | null;
  geraete: Geraetelage[];
  /** Je Tag des Fensters, wie viele Stücke dieses Typs zu haben sind. */
  tage: Tageslage[];
  /** Der erste Tag des Streifens als `JJJJ-MM-TT` — seine Beschriftung. */
  tageAb: string;
  /**
   * **Geht nur an einem Trägergerät hinaus** (Systemprüfung Befund 44,
   * 25.09.2026). Die Merkliste nimmt so etwas nicht an und sagt es beim
   * Merken, nicht erst beim Speichern (`merksperreFinden`).
   */
  anbaupflichtig?: boolean;
};

export type Gruppenpille = { id: string; name: string; zahl: number };

export type Nachschlag = {
  von: string;
  bis: string;
  gruppen: Gruppenpille[];
  zeilen: Typenlage[];
};

export type Frage = {
  wort: string;
  gruppe: string | null;
  von: string;
  bis: string;
  /**
   * Genau diese Maschinentypen — statt einer Suche.
   *
   * Die Merkliste hat ihre Posten schon gewählt und fragt nur noch, ob sie
   * frei sind (17.09.2026). Dieselbe Rechnung wie das Nachschlagen am
   * Telefon, damit „ab wann wieder frei" nicht an zwei Stellen entsteht.
   */
  typen?: string[];
};

/* ══════════════ Der Weg ══════════════ */

export async function nachschlagen(frage: Frage): Promise<Nachschlag> {
  if (ATTRAPPE) return { von: frage.von, bis: frage.bis, gruppen: [], zeilen: [] };

  const teile = new URLSearchParams({ von: frage.von, bis: frage.bis });
  if (frage.wort.trim()) teile.set("wort", frage.wort.trim());
  if (frage.gruppe) teile.set("gruppe", frage.gruppe);
  if (frage.typen?.length) teile.set("typ", frage.typen.join(","));

  const roh = await hole<Roh>(`/nachschlagen?${teile.toString()}`);
  return {
    von: txt(roh["von"]),
    bis: txt(roh["bis"]),
    gruppen: alsListe(roh["gruppen"]).map((g) => ({
      id: txt(g["id"]), name: txt(g["name"]), zahl: zahl(g["zahl"]),
    })),
    zeilen: alsListe(roh["zeilen"]).map((z) => ({
      typId: txt(z["typ_id"]),
      typ: txt(z["typ"]),
      gruppe: txt(z["gruppe"]),
      gruppeId: txt(z["gruppe_id"]),
      uebergruppe: txt(z["uebergruppe"]),
      lage: (txt(z["lage"]) || "nein") as Lage,
      textKey: txt(z["text_key"]),
      bestand: zahl(z["bestand"]),
      frei: zahl(z["frei"]),
      reserviert: zahl(z["reserviert"]),
      imAbschluss: zahl(z["im_abschluss"]),
      abschlussLaeuft: zahl(z["abschluss_laeuft"]),
      teils: zahl(z["teils"]),
      werkstatt: zahl(z["werkstatt"]),
      freiAb: tagOderNull(z["frei_ab"]),
      tage: alsStreifen(z["tage"]),
      tageAb: txt(z["tage_ab"]),
      ...(z["anbaupflichtig"] === true ? { anbaupflichtig: true } : {}),
      geraete: alsListe(z["geraete"]).map((g) => ({
        id: txt(g["id"]),
        kennung: txt(g["kennung"]),
        lage: (txt(g["lage"]) || "nein") as Lage,
        textKey: txt(g["text_key"]),
        tageVergeben: zahl(g["tage_vergeben"]),
        werkstatt: g["werkstatt"] === true,
        freiAb: tagOderNull(g["frei_ab"]),
        kunde: txt(g["kunde"]),
        reserviert: txt(alsObjekt(g["reserviert"])["vorgang"])
          ? {
            vorgang: txt(alsObjekt(g["reserviert"])["vorgang"]),
            kunde: txt(alsObjekt(g["reserviert"])["kunde"]),
            frist: txt(alsObjekt(g["reserviert"])["frist"]),
            imAbschluss: alsObjekt(g["reserviert"])["im_abschluss"] === true,
            abschlussLaeuft: alsObjekt(g["reserviert"])["abschluss_laeuft"] === true,
          }
          : null,
        tage: alsStreifen(g["tage"]),
      })),
    })),
  };
}

/**
 * Was zu Suchwort, Gerätegruppe und Zeitraum frei ist.
 *
 * **Die letzte Antwort bleibt stehen, solange die neue unterwegs ist**
 * (`placeholderData`). Am Telefon ist die Liste sonst bei jedem Buchstaben
 * leer, und der Schirm springt — dieselbe Überlegung wie bei `usePreis`.
 */
export function useNachschlagen(frage: Frage, an = true) {
  const typen = (frage.typen ?? []).join(",");
  return useQuery({
    queryKey: [
      "nachschlagen", frage.wort.trim(), frage.gruppe ?? "", frage.von, frage.bis, typen,
    ] as const,
    queryFn: () => nachschlagen(frage),
    // Der Abschluss aus der Merkliste fragt erst, wenn der Zeitraum steht —
    // vorher wäre die Antwort die auf eine andere Frage.
    enabled: an,
    placeholderData: (vorher: Nachschlag | undefined) => vorher,
  });
}

/* ══════════════ Tage am Telefon ══════════════ */

/** `2026-09-16` plus n Tage, wieder als `2026-09-16`. */
export function tagPlus(iso: string, tage: number): string {
  return tagPlusImBetrieb(iso, tage);
}

/** Heute als `JJJJ-MM-TT`, in deutscher Zeit (N8-02). */
export function heuteTag(): string {
  return heuteImBetrieb();
}

/**
 * Die Vorbelegung: **ab morgen, drei Tage** (09-mobile.md, Regel 11).
 *
 * Wer beim Kunden steht und fragt, meint fast nie heute — die Maschine
 * muss noch hinkommen. Und drei Tage ist die Dauer, bei der eine Zusage
 * etwas wert ist; ein Tag beantwortet nur die halbe Frage.
 */
export const VORGABE_TAGE = 3;

/** Der Wochentag zu einem ISO-Tag, als Index 0 = Montag. */
export function wochentagNr(iso: string): number {
  return (new Date(`${iso}T12:00:00`).getDay() + 6) % 7;
}

/** `17.09.` — kurz, ohne Jahr. Das Jahr steht am Telefon nie zur Debatte. */
export function tagKurz(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return `${d.getDate()}.${d.getMonth() + 1}.`;
}
